import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { ReactComponent as Filters } from "@assets/images/Filters.svg";
import AlertDialog from "@components/ui/ConfirmDialogue";
import {
  Button,
  InputAdornment,
  Menu,
  MenuItem,
  TextField,
} from "@mui/material";
import countryList from "react-select-country-list";
import { useLocation } from "react-router-dom";
import NotFound from "@components/ui/NotFound";
import ButtonLoader from "@components/ui/Loaders/ButtonLoader";
import {
  AllPreferenceInitialData,
  AllPreferences,
} from "data/dropdowndData/firstFormData";
import { useTranslation } from "react-i18next";
import MedicalViewMultipSelectDropdown from "@components/ui/customDropdowns/MedicalViewMultipSelectDropdown";
import { getAllFormFields, updateAllFormFields } from "api/preferences";
import ActualDietPlanLoader from "@components/ui/Loaders/ActualDietPlanLoader";

const tabs = ["General", "Physical", "Medical", "Dietary", "Exercise"];

const Preferences = () => {
  const location = useLocation();
  const [selectedTags, setSelectedTags] = useState({});
  const [options, setOptions] = useState(AllPreferences);
  const [isEditable, setIsEditable] = useState(false);
  const [originalTags, setOriginalTags] = useState({});
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);
  const { t } = useTranslation();
  const [openAccordion, setOpenAccordion] = useState(
    location?.state?.slideToShow || 0
  );
  const [errors, setErrors] = useState({});
  const countries = countryList().getData();
  const [anchorEl, setAnchorEl] = useState(null);
  const openTabs = Boolean(anchorEl);

  const InputFieldValidation = {
    yourAge: 3,
    height: 3,
    weight: 3,
    waistCircumference: 3,
    bodyFatPercentage: 3,
  };

  const validateInput = (key, value) => {
    const fieldLabel = t(`firstFormSteps.${key}.label`);
    if (key !== "dailyCalorieAdjustment") {
      const maxLength = InputFieldValidation[key];

      if (!value) {
        return t("validation.required", { field: fieldLabel });
      }
      if (value < 1) {
        return t("validation.greaterThanZero");
      }
      if (isNaN(value)) {
        return t("validation.validNumber");
      }
      if (value < 0) {
        return t("validation.nonNegative");
      }

      return null;
    }
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseTabs = () => {
    setAnchorEl(null);
  };

  const countryCodeToName = countries.reduce((acc, country) => {
    acc[country.value] = country.label;
    return acc;
  }, {});

  const handleClose = (key) => {
    if (key === "cancel") {
      setOpen(false);
    } else {
      setOpen(false);
      handleSavePreferences();
    }
  };

  const handleSavePreferences = async () => {
    setIsLoading(true);
    try {
      await updateAllFormFields(selectedTags);
      toast.info(t("toast.informationSubmit"));
    } catch (error) {
      toast.error(t("toast.failedSubmit"));
    } finally {
      setIsLoading(false);
    }
  };

  const removeTag = (key, tag) => {
    const updated = selectedTags[key].filter((t) => t !== tag);

    if (updated.length === 0) {
      setErrors((prev) => ({ ...prev, [key]: true }));
    }
    if (tag.toLowerCase() === "carnivore") {
      localStorage.removeItem("selectedDiet");
    }
    setSelectedTags((prev) => ({
      ...prev,
      [key]: updated,
    }));
  };

  const handleSelectChange = (key, value) => {
    setSelectedTags((prev) => ({ ...prev, [key]: value }));

    setErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      if (!value || value === "") {
        updatedErrors[key] = true;
      } else {
        delete updatedErrors[key];
      }
      return updatedErrors;
    });
  };

  const handleInputChange = (key, value) => {
    const error = validateInput(key, value);

    setSelectedTags((prev) => ({ ...prev, [key]: value }));

    setErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      if (error) {
        updatedErrors[key] = error;
      } else {
        delete updatedErrors[key];
      }
      return updatedErrors;
    });
  };

  const handleMultiSelectChange = (key, values) => {
    if (
      key === "healthConditions" ||
      key === "foodAllergies" ||
      key === "exoticDishes"
    ) {
      const lastSelected = values[values.length - 1];

      if (lastSelected === "None") {
        values = ["None"];
      } else {
        values = values.filter((v) => v !== "None");
      }
    }
    if (key === "selectableDays") {
      const lastSelected = values[values.length - 1];

      if (lastSelected === "All Days") {
        values = ["All Days"];
      } else {
        values = values.filter((v) => v !== "All Days");
      }
    }
    setSelectedTags((prev) => ({ ...prev, [key]: values }));

    setErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      if (!values || values.length === 0) {
        updatedErrors[key] = true;
      } else {
        delete updatedErrors[key];
      }
      return updatedErrors;
    });
  };

  const getForMFields = async () => {
    try {
      const data = await getAllFormFields();
      const tags = data?.selectedPreferences;
      setSelectedTags(tags ?? AllPreferenceInitialData);
    } catch (error) {
      console.error(
        "Error fetching diet plan:",
        error.response ? error.response.data : error.message
      );
    } finally {
      setFirstLoad(false);
    }
  };

  const handleClickOpen = () => {
    const newErrors = {};
    options.map((opt) => {
      opt.value?.forEach(({ key, type }) => {
        if (type === "numeric") {
          const error = validateInput(key, selectedTags[key]);
          if (error) {
            newErrors[key] = error;
          }
        }
        if (
          type === "multi-select" &&
          (!selectedTags[key] || selectedTags[key].length === 0)
        ) {
          newErrors[key] = "Please select at least one option";
        } else if (type === "select" && !selectedTags[key]) {
          newErrors[key] = "Please select an option";
        }
      });
    });
    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      setOpen(true);
    } else {
      toast.error(t("toast.OopsReview"));
    }
  };

  useEffect(() => {
    getForMFields();
  }, []);

  return (
    <>
      {firstLoad ? (
        <ActualDietPlanLoader preferences={true} />
      ) : (
        <div className="bg-white shadow-lg p-4 md:p-6 rounded-lg">
          {/* Header Section */}
          <div className="flex justify-between items-center gap-3 mb-4 md:mb-6">
            <h2 className="text-xl font-semibold text-[#0575E6]">
              {t("allPreferencesModule.preferencesHeading")}
            </h2>

            <div className="flex gap-2 w-full sm:w-auto items-center justify-end">
              {options?.length > 0 && (
                <>
                  {/* Desktop Save Button */}
                  <button
                    className={`hidden md:flex items-center justify-center min-w-[60px] px-8 py-2 bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] text-white rounded-full text-sm font-medium transition-colors hover:from-blue-700 hover:to-blue-500 min-w-[150px] min-h-[40px] ${
                      isLoading ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                    disabled={isLoading}
                    onClick={handleClickOpen}
                  >
                    {isLoading ? (
                      <ButtonLoader />
                    ) : (
                      t("allPreferencesModule.Save preferences")
                    )}
                  </button>

                  {/* Mobile Save Button */}
                  <button
                    className={`md:hidden bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] min-w-[70px] text-white p-2 rounded-md border border-[#0575E6] transition-colors hover:bg-blue-100 ${
                      isLoading ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                    onClick={handleClickOpen}
                    disabled={isLoading}
                  >
                    {/* <Tick className="w-4 h-4" /> */}
                    {isLoading ? <ButtonLoader /> : "Save"}
                  </button>
                </>
              )}

              {/* Mobile Tabs Dropdown */}
              <div className="md:hidden">
                <Button
                  id="basic-button"
                  aria-controls={openTabs ? "basic-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={openTabs ? "true" : undefined}
                  onClick={handleClick}
                  sx={{
                    background:
                      "linear-gradient(150deg, #0771EF 12.89%, #15AAFC 85.22%)",
                    borderRadius: "4px",
                    padding: "6px",
                    minWidth: "70px",
                    height: "42px",
                  }}
                >
                  <Filters className="w-6 h-6" />
                </Button>

                <Menu
                  id="basic-menu"
                  anchorEl={anchorEl}
                  open={openTabs}
                  onClose={handleCloseTabs}
                  MenuListProps={{
                    "aria-labelledby": "basic-button",
                  }}
                  PaperProps={{
                    style: {
                      marginTop: "8px",
                    },
                  }}
                >
                  {tabs.map((item, index) => (
                    <MenuItem
                      key={item}
                      onClick={() => {
                        setOpenAccordion(index);
                        handleCloseTabs();
                      }}
                      selected={index === openAccordion}
                      sx={{
                        bgcolor:
                          index === openAccordion
                            ? "action.selected"
                            : "background.paper",
                        "&:hover": {
                          bgcolor: "action.hover",
                        },
                      }}
                    >
                      {t(`allPreferencesModule.moduleLabels.${item}`)}
                    </MenuItem>
                  ))}
                </Menu>
              </div>
            </div>
          </div>

          {/* Desktop Tabs */}
          <div className="hidden md:flex bg-white rounded-full p-1 border border-[#E9E9E9] w-full gap-2 my-4 md:my-6">
            {tabs?.map((item, index) => {
              const isActive = index === openAccordion;
              return (
                <button
                  key={item}
                  onClick={() => setOpenAccordion(index)}
                  className={`relative flex-1 py-2 text-sm rounded-full capitalize transition-all duration-300 text-center ${
                    isActive ? "text-white" : "text-black bg-white"
                  }`}
                  style={{
                    background: isActive
                      ? "linear-gradient(150deg, #0771EF 12.89%, #15AAFC 85.22%)"
                      : "transparent",
                  }}
                >
                  <span className="text-sm font-medium">
                    {t(`allPreferencesModule.moduleLabels.${item}`)}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Preferences Content */}
          {options?.length > 0 ? (
            options?.map((opt, index) => {
              if (index !== openAccordion) return null;
              return (
                <div
                  key={index}
                  className="border border-gray-200 rounded-lg shadow-sm"
                >
                  {/* Table Header */}
                  <div className="hidden md:grid bg-gradient-to-r from-blue-600 to-blue-400 text-white p-4 font-semibold">
                    <div className="grid grid-cols-3 gap-4">
                      <span>{t(`allPreferencesModule.Options`)}</span>
                      <span>{t(`allPreferencesModule.Preferences`)}</span>
                      <span>{t(`allPreferencesModule.Selected`)}</span>
                    </div>
                  </div>

                  {/* Mobile Header */}
                  <div className="md:hidden bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] text-white p-3 font-semibold text-center">
                    {t(
                      `allPreferencesModule.moduleLabels.${tabs[openAccordion]}`
                    )}
                  </div>

                  {/* Preferences List */}
                  <div className="p-3 md:p-4 space-y-4 md:space-y-6">
                    {opt?.value?.map(({ label, key, options, type }) => {
                      if (type !== "country-select") {
                        return (
                          <div
                            key={key}
                            className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 p-3 md:p-4 bg-gray-50 rounded-lg border border-gray-100"
                          >
                            {/* Field Label */}
                            <div className="md:col-span-1">
                              {label === "Weight" ? (
                                <div className="flex flex-col gap-2">
                                  <span className="text-sm md:text-base font-medium text-gray-700">
                                    {t("allPreferencesModule.fields.weight")}
                                  </span>
                                  {/* <Link
                                    to="/advise"
                                    className="bg-blue-600 text-white text-xs px-3 py-1 rounded-md w-fit hover:bg-blue-700 transition-colors"
                                  >
                                    {t("allPreferencesModule.Click here to get advice")}
                                  </Link> */}
                                </div>
                              ) : (
                                <span className="text-sm md:text-base font-medium text-gray-700">
                                  {t(`allPreferencesModule.fields.${key}`)}
                                </span>
                              )}
                            </div>

                            {/* Input/Dropdown */}
                            <div className="md:col-span-1">
                              <div className="w-full">
                                {type === "multi-select" ? (
                                  <MedicalViewMultipSelectDropdown
                                    options={options?.map((opt) => ({
                                      value: opt,
                                      label: t(
                                        `allPreferencesModule.options.${key}.${opt}`
                                      ),
                                    }))}
                                    selectedValues={selectedTags[key] || []}
                                    onSelect={(selectedOptions) => {
                                      const originalValues =
                                        selectedOptions.map((opt) =>
                                          typeof opt === "object"
                                            ? opt.value
                                            : opt
                                        );
                                      handleMultiSelectChange(
                                        key,
                                        originalValues
                                      );
                                    }}
                                    hasError={errors[key]}
                                    label={t(
                                      `allPreferencesModule.fields.${key}`
                                    )}
                                    placeholder={t(
                                      "preferences.validation.onlyOne"
                                    )}
                                    isEditable={true}
                                    isFistForm={true}
                                  />
                                ) : type === "select" ? (
                                  <>
                                    <select
                                      className={`w-full border p-2 rounded-md h-10 text-sm outline-none ${
                                        errors[key]
                                          ? "border-red-500 text-red-500 bg-white"
                                          : "text-black bg-white"
                                      }`}
                                      value={
                                        Array.isArray(selectedTags[key])
                                          ? selectedTags[key][
                                              selectedTags[key].length - 1
                                            ]
                                          : selectedTags[key] || ""
                                      }
                                      onChange={(e) =>
                                        handleSelectChange(key, e.target.value)
                                      }
                                    >
                                      <option value="" disabled>
                                        {t("preferences.validation.onlyOne")}
                                      </option>
                                      {options.map((option) => (
                                        <option
                                          key={option}
                                          value={option}
                                          className="text-black"
                                        >
                                          {t(
                                            `allPreferencesModule.options.${key}.${option}`
                                          )}
                                        </option>
                                      ))}
                                    </select>
                                    {errors[key] && (
                                      <div className="text-red-500 text-xs mt-1 ml-1">
                                        {errors[key]}
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    {key === "weight" ? (
                                      <div className="w-full">
                                        <TextField
                                          type="number"
                                          variant="outlined"
                                          fullWidth
                                          size="small"
                                          value={selectedTags.weight || ""}
                                          onChange={(e) =>
                                            handleInputChange(
                                              "weight",
                                              e.target.value,
                                              "Weight"
                                            )
                                          }
                                          onKeyDown={(e) => {
                                            if (
                                              ["e", "E", "+", "-"].includes(
                                                e.key
                                              )
                                            ) {
                                              e.preventDefault();
                                            }
                                          }}
                                          sx={{
                                            "& .MuiOutlinedInput-root": {
                                              fontSize: "0.875rem",
                                              color: "black",
                                              height: "40px",
                                              paddingRight: "5px",
                                              "& fieldset": {
                                                borderColor: errors.weight
                                                  ? "#EF4444"
                                                  : "#e5e7eb",
                                                borderRadius: "0.375rem",
                                              },
                                              "&:hover fieldset": {
                                                borderColor: errors.weight
                                                  ? "#EF4444"
                                                  : "#e5e7eb",
                                              },
                                              "&.Mui-focused fieldset": {
                                                borderColor: errors.weight
                                                  ? "#EF4444"
                                                  : "#e5e7eb",
                                                boxShadow: "none",
                                                outline: "none",
                                                borderWidth: "1px",
                                              },
                                              "& input::placeholder": {
                                                fontSize: "0.75rem",
                                                opacity: 1,
                                                color: "#9CA3AF",
                                              },
                                            },
                                          }}
                                          InputProps={{
                                            endAdornment: (
                                              <InputAdornment position="end">
                                                <div className="flex items-center mr-1">
                                                  {["kg", "Lbs"].map((unit) => (
                                                    <button
                                                      key={unit}
                                                      onClick={() => {
                                                        setSelectedTags(
                                                          (prev) => {
                                                            const newUnit =
                                                              unit;
                                                            if (prev.weight) {
                                                              const convertedWeight =
                                                                newUnit ===
                                                                "Lbs"
                                                                  ? prev.weight *
                                                                    2.20462
                                                                  : prev.weight /
                                                                    2.20462;
                                                              return {
                                                                ...prev,
                                                                weightUnit:
                                                                  newUnit,
                                                                weight:
                                                                  Math.round(
                                                                    convertedWeight
                                                                  ),
                                                              };
                                                            }
                                                            return {
                                                              ...prev,
                                                              weightUnit:
                                                                newUnit,
                                                            };
                                                          }
                                                        );
                                                      }}
                                                      className={`px-2 py-0.5 border rounded-md mx-0.5 text-xs ${
                                                        selectedTags.weightUnit ===
                                                        unit
                                                          ? "bg-blue-500 text-white"
                                                          : "bg-white text-black border-gray-300"
                                                      }`}
                                                      type="button"
                                                      disabled={
                                                        unit ===
                                                        selectedTags.weightUnit
                                                      }
                                                    >
                                                      {t(
                                                        `preferences.units.${unit.toLowerCase()}`
                                                      )}
                                                    </button>
                                                  ))}
                                                </div>
                                              </InputAdornment>
                                            ),
                                          }}
                                          placeholder="Weight"
                                        />
                                      </div>
                                    ) : (
                                      <input
                                        type="number"
                                        min="1"
                                        className={`w-full border p-2 rounded-md h-10 text-sm focus:outline-none ${
                                          errors[key]
                                            ? "border-red-500 text-red-500"
                                            : "text-black"
                                        }`}
                                        value={selectedTags[key] || ""}
                                        onChange={(e) =>
                                          handleInputChange(key, e.target.value)
                                        }
                                        onKeyDown={(e) => {
                                          if (
                                            ["e", "E", "+", "-"].includes(e.key)
                                          ) {
                                            e.preventDefault();
                                          }
                                        }}
                                        placeholder={`${t(
                                          `allPreferencesModule.fields.${key}`
                                        )}`}
                                      />
                                    )}
                                    {errors[key] && (
                                      <div className="text-red-500 text-xs mt-1 ml-1">
                                        {errors[key]}
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>

                            {/* Selected Tags */}
                            <div className="md:col-span-1">
                              <div className="flex flex-wrap gap-2 min-h-[40px] items-start">
                                {type !== "numeric" && (
                                  <>
                                    {selectedTags[key] ? (
                                      Array.isArray(selectedTags[key]) ? (
                                        selectedTags[key]?.map((tag, index) => (
                                          <span
                                            key={tag}
                                            className="inline-flex items-center bg-blue-100 text-blue-700 px-2 py-1 rounded-md text-xs max-w-full break-words"
                                            style={{ wordBreak: "break-word" }}
                                          >
                                            <span className="flex-shrink min-w-0">
                                              {t(
                                                `allPreferencesModule.options.${key}.${tag}`
                                              )}
                                            </span>
                                            <button
                                              onClick={() =>
                                                removeTag(
                                                  key,
                                                  selectedTags[key][index]
                                                )
                                              }
                                              className="ml-1 text-blue-500 hover:text-blue-800 flex-shrink-0"
                                            >
                                              ✕
                                            </button>
                                          </span>
                                        ))
                                      ) : (
                                        <span className="inline-flex bg-blue-100 text-blue-700 px-2 py-1 rounded-md text-xs max-w-full break-words">
                                          {key !== "selectedCountry"
                                            ? t(
                                                `allPreferencesModule.options.${key}.${selectedTags[key]}`
                                              )
                                            : countryCodeToName[
                                                selectedTags[key]
                                              ]}
                                        </span>
                                      )
                                    ) : (
                                      <span className="text-gray-400 text-xs">
                                        {t(
                                          `allPreferencesModule.No option selected`
                                        )}
                                      </span>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      }
                    })}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center py-8">
              <NotFound
                message={t("allPreferencesModule.No Preferences Found")}
              />
            </div>
          )}
        </div>
      )}

      <AlertDialog
        open={open}
        handleClickOpen={handleClickOpen}
        handleClose={handleClose}
        isLoading={isLoading}
        heading={t("allPreferencesModule.dialog.heading")}
        message={t("allPreferencesModule.dialog.message")}
      />
    </>
  );
};

export default Preferences;
